import React from "react";

import { AVAILABLE_NUMBER_SHORTCUTS } from "@/components/suggestions/actions/useSuggestionsActions";
import { indexLabels, insertedNodeType } from "@/components/suggestions/utils/libraryItem";
import { ariaKeyShortcuts, displayHotkey } from "@/utils/hotkeys";

import type {
  OperatorSuggestionProps,
  Suggestion,
  SuggestionItem,
  GroupSuggestionProps,
  SuggestionsGroup,
} from "@/components/suggestions/types";
import type { useTranslations } from "next-intl";

export type SuggestionsTranslateFn = ReturnType<typeof useTranslations<"suggestions">>;

export function getSuggestionTitle(option: Suggestion, t: SuggestionsTranslateFn): React.ReactNode {
  switch (option.type) {
    case "multi":
      return getMultiSuggestionTitle(option, t);

    case "single":
      return getSingleSuggestionTitle(option, t);

    case "editNode":
      return t("editNode");

    case "clipboardCommand":
      return t(`clipboardCommand.${option.command}`);

    case "collapseBlock": {
      const [from, to] = option.lineRange;
      return t("collapseBlock", {
        verb: option.collapsed ? "show" : "hide",
        count: option.positions.length,
        from,
        to,
      });
    }

    case "color":
      return t(option.target === "node" ? "nodeColor" : "color");

    case "selectionPrompt":
      return t("selectionPrompt");

    case "computedCommand":
      return t(`computedCommand.${option.command}`);

    case "mention":
      return option.user.name;

    default:
      return "";
  }
}

function isMinusItem(item: SuggestionItem) {
  return insertedNodeType(item) === "operatorSymbol" && item.key === "symbol-subtraction";
}

// a freshly built index suggestion (e.g. x_1) only carries its subscript in
// its labels, which never reach the title text; spell it out so the row reads
// "Variable x index 1" instead of the base "Variable x"
function getIndexLabelSuffix(item: SuggestionItem) {
  if (!item.isNewNode) return "";
  const [index, ...rest] = indexLabels(item);
  if (index == null || rest.length > 0) return "";
  return ` index ${index}`;
}

export function getMultiSuggestionTitle(
  option: Extract<Suggestion, { type: "multi" }>,
  t: SuggestionsTranslateFn,
): React.ReactNode {
  return (
    <>
      {option.items.map((item, idx) => {
        let text: string;
        let separator: React.ReactNode = <span className="text-gray-500"> × </span>;

        switch (insertedNodeType(item)) {
          case "constant":
          case "variable":
          case "number":
            text = (item.displayName ?? (item.intlName && t(item.intlName))) || item.symbol;
            text += getIndexLabelSuffix(item);
            break;
          case "operator":
            {
              text = "";
              const itemProps = (item.props ?? {}) as OperatorSuggestionProps;
              if (itemProps.operatorType === "power") {
                text = item.value === "2" ? " squared" : ` power ${item.value}`;
                separator = "";
              }
            }
            break;
          default:
            text = isMinusItem(item)
              ? "Negative"
              : (item.displayName ?? (item.intlName && t(item.intlName)) ?? "");
        }

        if (idx > 0) {
          const prevItem = option.items[idx - 1];
          if (insertedNodeType(prevItem) === "function") {
            separator = " of ";
          } else if (isMinusItem(prevItem)) {
            separator = " ";
          }
        }

        return idx === 0 || !text ? (
          text
        ) : (
          // eslint-disable-next-line react/no-array-index-key
          <React.Fragment key={idx}>
            {separator}
            {text}
          </React.Fragment>
        );
      })}
    </>
  );
}

export function getSingleSuggestionTitle(
  option: Extract<Suggestion, { type: "single" }>,
  t: SuggestionsTranslateFn,
): React.ReactNode {
  let text = option.item.displayName ?? (option.item.intlName && t(option.item.intlName));

  if (insertedNodeType(option.item) === "group") {
    const itemProps = (option.item.props ?? {}) as GroupSuggestionProps;

    if (option.action?.type === "open-group" || option.action?.type === "close-group") {
      if (itemProps.closingSymbol && itemProps.openingSymbol) {
        text = t("group.close", { name: text });
      } else if (itemProps.closingSymbol || itemProps.openingSymbol) {
        text = t("group.open", { name: text });
      }
    }
  }

  return text;
}

export function getSuggestionHotkey(itemIndex: number): string | undefined {
  if (itemIndex < AVAILABLE_NUMBER_SHORTCUTS.length) {
    return displayHotkey(`suggestion-${AVAILABLE_NUMBER_SHORTCUTS[itemIndex]}`);
  }

  return undefined;
}

export function getSuggestionAriaHotkey(itemIndex: number): string | undefined {
  if (itemIndex < AVAILABLE_NUMBER_SHORTCUTS.length) {
    return ariaKeyShortcuts(`suggestion-${AVAILABLE_NUMBER_SHORTCUTS[itemIndex]}`);
  }

  return undefined;
}

export function getSuggestionSubTitle(
  option: Suggestion,
  t: SuggestionsTranslateFn,
): string | undefined {
  if (option.categoryIntlName) {
    return t(option.categoryIntlName);
  }

  if (option.type === "mention") {
    return option.user.username ? `@${option.user.username}` : undefined;
  }

  const category = option.type !== "single" ? "" : option.item.category;
  return Array.isArray(category) ? category[0] : category;
}

export function hasNestedItems(item: Suggestion): item is Suggestion & {
  type: "single";
  nestedItems: SuggestionsGroup[];
} {
  return item.type === "single" && !!item.nestedItems && item.nestedItems.length > 0;
}

const GRID_POPUP_GROUPS = ["symbols", "phantomHint"] as const;
const PHANTOM_GROUPS = ["phantomHint"] as const;

/**
 * The green ring that marks a row served from a library while the JSON files coexist with the
 * libraries, so the two sources can be told apart; nothing for any other suggestion.
 */
export function libraryRingClass(suggestion: Suggestion): string | undefined {
  const fromLibrary =
    suggestion.type === "single"
      ? suggestion.item.type === "library"
      : suggestion.type === "multi" && suggestion.items.some((item) => item.type === "library");

  return fromLibrary
    ? "ring-inset ring-offset-1 ring-1 ring-green-300 dark:ring-green-800"
    : undefined;
}

export function isGridOptionSiggestion(item: Suggestion | undefined): boolean {
  return item?.popupGroup ? GRID_POPUP_GROUPS.includes(item.popupGroup) : false;
}

export function isPhantomOptionSiggestion(item: Suggestion | undefined): boolean {
  return item?.popupGroup ? PHANTOM_GROUPS.includes(item.popupGroup) : false;
}

export function getNestedList(
  listGroups: SuggestionsGroup[],
  nestedListId: string | null,
): Suggestion[] {
  for (const group of listGroups) {
    for (const item of group.items) {
      if (item.type === "single" && item.nestedItems && item.nestedKey === nestedListId) {
        return item.nestedItems.flatMap((nestedGroup) => nestedGroup.items);
      }
    }
  }

  return listGroups.flatMap((group) => group.items);
}
